import React, { useCallback, useMemo, useRef } from "react";
import styled from "styled-components";

import MarkdownContent from "../../../components/markdown/MarkdownContent";
import Avatar from "../../../components/avatar/Avatar";
import DropdownMenu, {
  DropdownMenuItem,
} from "../../../components/dropdown-menu/DropdownMenu";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../../components/date-display/DateDisplay";
import StatusBadge from "../../../components/status-badge/StatusBadge";
import ToolTip from "../../../components/tooltip/ToolTip";
import useCopyToClipboard from "../../../hooks/useCopyToClipboard";
import type { NoteDetail } from "../../../app/services/notes";
import { buildNoteLink, toMarkdownLink } from "../../../utils/deepLink";
import { toggleCheckboxInText } from "../../projects/components/TaskBoard/taskBoardUtils";
import {
  MISSING_NOTE_HREF,
  NOTE_HREF_PREFIX,
  extractNoteIdFromHref,
  extractToc,
  resolveWikilinks,
} from "../utils/markdown";
import { downloadNoteAsMarkdown } from "../utils/export";

/**
 * ノート本文の表示。
 *
 * - `[[wikilink]]` は事前処理で内部リンク（#note-<id>）へ変換し、
 *   クリックはコンテナのイベント委任で SPA 遷移する
 * - h1〜h3 から生成したページ内目次を本文上部に表示する
 * - 編集権限があればチェックボックスをクリックで ON/OFF できる
 *   （タスクボードと同じ「編集モードに入らずトグル」の振る舞い）
 * - 編集 / リンクをコピー / ダウンロード / 削除はタイトル行右端
 *   （読み物幅の右端）の3点リーダーメニューにまとめる
 * - 「リンクをコピー」はこのノートへのディープリンクを Markdown 形式
 *   （[ノート名](URL)）で発行する（トークルーム / DM 等に貼って参照できる。
 *   projectId があればプロジェクトスコープ URL）
 */

const ViewWrapper = styled.article`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  /* 読み物として快適な行長（Obsidian の readable line length 相当）。
     本文幅そのものを 720px にし、左右の余白は画面が狭いときだけ取る */
  width: min(720px, 100% - 32px);
  margin: 0 auto;
  padding: 24px 0 96px;

  /* 内部ノートリンク（wikilink 由来）のスタイル */
  a[href^="${NOTE_HREF_PREFIX}"] {
    color: var(--soremo-blue);
    cursor: pointer;
  }
  /* 未解決の wikilink はグレー + 破線で区別する */
  a[href="${MISSING_NOTE_HREF}"] {
    color: var(--soremo-light-gray);
    border-bottom: 1px dashed var(--soremo-light-gray);
    cursor: default;
  }
`;

const TocBox = styled.nav`
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  padding: 12px 16px;
  background: #fcfcfc;
`;

const TocItemButton = styled.button<{ $level: number }>`
  display: block;
  width: 100%;
  padding: 2px 0;
  padding-left: ${({ $level }) => ($level - 1) * 16}px;
  border: none;
  background: transparent;
  color: var(--soremo-gray);
  cursor: pointer;
  text-align: left;

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
    }
  }
`;

export interface NoteViewProps {
  note: NoteDetail;
  wikilinkMap: Record<string, string>;
  onNavigateToNote: (noteId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** チェックボックストグル等で本文を更新する（編集権限がある場合のみ渡す） */
  onContentChange?: (newContent: string) => void;
  /** カード表示への切替（直接更新権限がある場合のみ渡す） */
  onSwitchStyle?: () => void;
  /** リンクコピーをプロジェクトスコープ URL（?tab=notes&note=）にする場合に渡す */
  projectId?: string;
  /**
   * 下書き（未提出の変更提案）の表示: タイトル横に「下書き」チップを出し、
   * 実体ページが無いためリンクコピーを隠す
   */
  draftMode?: boolean;
  /** 削除メニューのラベル差し替え（下書きでは「取り下げ」） */
  deleteLabel?: string;
}

const NoteView: React.FC<NoteViewProps> = ({
  note,
  wikilinkMap,
  onNavigateToNote,
  onEdit,
  onDelete,
  onContentChange,
  onSwitchStyle,
  projectId,
  draftMode = false,
  deleteLabel = "削除",
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const resolvedContent = useMemo(
    () => resolveWikilinks(note.content, wikilinkMap),
    [note.content, wikilinkMap],
  );

  const toc = useMemo(() => extractToc(note.content), [note.content]);

  // 内部ノートリンクのクリックを委任で拾って SPA 遷移する
  const handleContentClick = useCallback(
    (e: React.MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (href && href.startsWith(NOTE_HREF_PREFIX)) {
        e.preventDefault();
        const noteId = extractNoteIdFromHref(href);
        if (noteId) onNavigateToNote(noteId);
      }
    },
    [onNavigateToNote],
  );

  // チェックボックスのトグル: 生の本文（wikilink 変換前）に対して置換して保存する。
  // wikilink 変換はチェックボックス行を増減させないため index はそのまま使える。
  const handleCheckboxToggle = useCallback(
    (index: number, checked: boolean) => {
      onContentChange?.(toggleCheckboxInText(note.content, index, checked));
    },
    [note.content, onContentChange],
  );

  // 目次クリック: 本文中の N 番目の見出しへスクロール
  const handleTocClick = useCallback((index: number) => {
    const container = contentRef.current;
    if (!container) return;
    const headings = container.querySelectorAll("h1, h2, h3");
    headings[index]?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // タイトル行右端の3点リーダーメニュー
  // （リンクコピー / ダウンロードは閲覧できるノートなら誰でも可）
  const { copy } = useCopyToClipboard({
    successMessage: "リンクをコピーしました",
  });
  const menuItems = useMemo(() => {
    const items: DropdownMenuItem[] = [];
    if (onEdit) {
      items.push({ label: "編集", icon: "edit", onClick: onEdit });
    }
    if (onSwitchStyle) {
      items.push({
        label: "カード表示に切り替え",
        icon: "style",
        onClick: onSwitchStyle,
      });
    }
    // 下書きは実体ページ（/notes/:id）が無いためリンクコピーは出さない
    if (!draftMode) {
      items.push({
        label: "リンクをコピー",
        icon: "link",
        onClick: () => {
          void copy(
            toMarkdownLink(note.title, buildNoteLink(note.id, projectId)),
          );
        },
      });
    }
    items.push({
      label: "ダウンロード",
      icon: "download",
      onClick: () => downloadNoteAsMarkdown(note.title, note.content),
    });
    if (onDelete) {
      items.push({ label: deleteLabel, icon: "delete", onClick: onDelete });
    }
    return items;
  }, [
    onEdit,
    onDelete,
    onSwitchStyle,
    note.id,
    note.title,
    note.content,
    projectId,
    copy,
    draftMode,
    deleteLabel,
  ]);

  return (
    <ViewWrapper>
      <div>
        <div className="u-row-between u-gap8 u-mb8">
          <div className="u-row u-gap8 u-items-center">
            <h1 className="heading-spacing-21 u-line-height-150">
              {note.title}
            </h1>
            {draftMode && (
              <ToolTip description="未提出の変更提案です。プルリクエストを送信するまで他のメンバーには公開されません">
                <StatusBadge label="下書き" size="small" />
              </ToolTip>
            )}
          </div>
          <DropdownMenu items={menuItems} size="small" />
        </div>
        <div className="u-row u-gap24 u-items-center">
          <span className="bodytext-11 u-text-light-gray">
            {note.modified && (
              <DateDisplay
                date={note.modified}
                type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME}
              />
            )}
          </span>
          {note.updated_by && (
            <div className="u-row u-gap8 u-items-center">
              <Avatar
                size={32}
                src={note.updated_by.avatar || undefined}
                name={note.updated_by.display_name}
                tooltipText={note.updated_by.display_name}
              />
              <span className="bodytext-11 u-text-light-gray">
                {note.updated_by.display_name}
              </span>
            </div>
          )}
        </div>
      </div>

      {toc.length >= 2 && (
        <TocBox aria-label="目次">
          <p className="heading-13 u-text-light-gray u-mb8">目次</p>
          {toc.map((item) => (
            <TocItemButton
              key={`${item.index}-${item.text}`}
              $level={item.level}
              className="bodytext-13 u-line-height-150"
              onClick={() => handleTocClick(item.index)}
            >
              {item.text}
            </TocItemButton>
          ))}
        </TocBox>
      )}

      <MarkdownContent
        content={resolvedContent}
        variant="document"
        breaks
        zoomableImages
        attachmentDisplay="preview-card"
        className="u-text-justify"
        containerRef={contentRef}
        onClick={handleContentClick}
        interactiveCheckboxes={!!onContentChange}
        onCheckboxToggle={onContentChange ? handleCheckboxToggle : undefined}
      />
    </ViewWrapper>
  );
};

export default NoteView;
